import autoBind from 'auto-bind'
import Patient from 'models/Patient'
import Device from 'models/Device'
import Usage from 'models/Usage'
import {Environment} from './environments'
import Activity from 'models/Activity'
import User from 'models/User'
import Pain from 'models/Pain'
import {SearchResult} from 'redux/modules/search'
import AdministratorHistory from 'models/AdministratorHistory'
import {CsvImportFailureSummary, CsvImportSuccessSummary} from 'models/CsvImportSummary'
import {DeviceDetail, DeviceTimelineEntry, DeviceTransitionStatus} from 'redux/modules/deviceLookup'
import {CsvValidationSummary} from 'models/CsvValidationSummary'

enum Method {
  Get = 'GET',
  Post = 'POST',
  Patch = 'PATCH',
  Put = 'PUT',
  Delete = 'DELETE',
}

export const UnauthorizedError = new Error('Unauthorized')
export const ForbiddenError = new Error('Forbidden')
export const FORBIDDEN_MESSAGE = "You don't have permissions to access"
// A fresh instance is thrown per 503 response rather than reusing a shared singleton —
// a shared mutable object would let concurrent 503 responses clobber each other's message.
export class MaintenanceError extends Error {}

// A fresh instance is thrown per 400 response (see handleResponse) rather than reusing a
// shared singleton, since this error now carries per-request data (details/batchSummary) —
// a shared mutable object would let concurrent 400 responses clobber each other's data.
export class ApiError extends Error {
  details: string[]
  batchSummary?: CsvImportFailureSummary
  constructor(message: string, details: string[] = [], batchSummary?: CsvImportFailureSummary) {
    super(message)
    this.name = 'ApiError'
    this.details = details
    this.batchSummary = batchSummary
  }
}

class Api {
  environment: Environment
  token?: string
  searchAbortController?: AbortController
  validateAbortController?: AbortController

  /**
   * Creates a new Api object for leveraging the backend REST webservice powering this admin app
   * @param environment the api environment to use
   */
  constructor(environment: Environment) {
    autoBind(this)
    this.environment = environment
  }

  setToken(token: string | undefined) {
    this.token = token
  }

  async login(email: string, password: string) {
    try {
      const response = await this.request(Method.Post, '/login', {email, password})
      const user = new User(response)
      if (user.admin === undefined) {
        // The backend contract (see /login) guarantees this field — its absence means the
        // response shape drifted, not that this user was deliberately marked non-admin.
        // Still deny access (fail closed), but log it distinctly so it isn't mistaken for
        // an intentional rejection.
        console.error(`Login response for ${email} is missing the admin flag`)
      }
      if (!user.admin) {
        return {status: 'error', message: FORBIDDEN_MESSAGE, code: 403}
      }
      return user
    } catch (error) {
      let response = {status: 'error', message: 'Not Provided', code: 500}
      if (error === UnauthorizedError) {
        response.message = 'Invalid email or password'
        response.code = 401
      } else if (error === ForbiddenError) {
        response.message = FORBIDDEN_MESSAGE
        response.code = 403
      } else if (error instanceof MaintenanceError) {
        response.message = 'The app is under maintenance, please try again later.'
        response.code = 503
      }
      return response
    }
  }

  async search(query: string): Promise<SearchResult> {
    if (this.searchAbortController) {
      this.searchAbortController.abort()
    }
    const searchAbortController = new AbortController()
    this.searchAbortController = searchAbortController
    try {
      const response = await this.request(
        Method.Post,
        '/search',
        {query},
        searchAbortController.signal,
      )
      return {
        users: response.users.map((json: any) => new User(json)),
        devices: response.devices.map((json: any) => new Device(json)),
        patients: response.patients.map((json: any) => new Patient(json)),
      }
    } finally {
      if (this.searchAbortController === searchAbortController) {
        this.searchAbortController = undefined
      }
    }
  }

  async getAllPain() {
    const response = await this.request(Method.Get, '/pain/all')
    return response.map((json: any) => new Pain(json))
  }

  async getPain(patientId: string) {
    const response = await this.request(Method.Get, `/pain?patientId=${patientId}`)
    return response.map((json: any) => new Pain(json))
  }

  async getUsers() {
    const response = await this.request(Method.Get, '/users')
    return response.map((json: any) => new User(json))
  }

  async getUsersPage(page: number = 1, limit: number = 100): Promise<{users: User[], total: number, page: number, limit: number}> {
    const response = await this.request(Method.Get, `/users/paginated?page=${page}&limit=${limit}`)
    return {
      users: response.users.map((json: any) => new User(json)),
      total: response.total,
      page: response.page,
      limit: response.limit,
    }
  }

  async createUser(user: User) {
    await this.request(Method.Post, '/users', user.asJson())
  }

  async deleteUser(id: string) {
    await this.request(Method.Delete, `/users/${id}`)
  }

  async getAllActivity() {
    const response = await this.request(Method.Get, '/activity')
    return response.map((json: any) => new Activity(json))
  }

  async getActivity(deviceId: string) {
    const response = await this.request(Method.Get, `/devices/${deviceId}/activity`)
    return response.map((json: any) => new Activity(json))
  }

  async getAllUsage() {
    const response = await this.request(Method.Get, '/usage')
    return response.map((json: any) => new Usage(json))
  }

  async getUsage(deviceId: string) {
    const response = await this.request(Method.Get, `/devices/${deviceId}/usage`)
    return response.map((json: any) => new Usage(json))
  }

  async getDevices() {
    const response = await this.request(Method.Get, '/devices')
    return response.map((json: any) => new Device(json))
  }

  async getDevicesPage(page: number = 1, limit: number = 100): Promise<{devices: Device[], total: number, page: number, limit: number}> {
    const response = await this.request(Method.Get, `/devices/paginated?page=${page}&limit=${limit}`)
    return {
      devices: response.devices.map((json: any) => new Device(json)),
      total: response.total,
      page: response.page,
      limit: response.limit,
    }
  }

  async getDeviceNameByPatientId(patientId: string): Promise<{name: string, udid: string} | undefined> {
    const endpoint = `/devices/${patientId}/name`
    return await this.request(Method.Get, endpoint)
  }

  async addDevice(device: Device, patientId: string) {
    const response = await this.request(Method.Post, '/devices', {
      name: device.name,
      udid: device.udid,
      patientId: patientId,
    })
    return new Device(response)
  }

  async checkDeviceNameAvailability(
    name: string,
    patientId: string,
  ): Promise<string[] | undefined> {
    const endpoint = `/devices/${name}/${patientId}/nameAvailability`
    return await this.request(Method.Get, endpoint)
  }

  async deleteDevice(id: string) {
    await this.request(Method.Delete, `/devices/${id}`)
  }

  async getDeviceDetail(udid: string): Promise<DeviceDetail> {
    return this.request(Method.Get, `/devices/${udid}/detail`)
  }

  async getDeviceTimeline(udid: string): Promise<DeviceTimelineEntry[]> {
    return this.request(Method.Get, `/devices/${udid}/timeline`)
  }

  async transitionDevice(
    id: string,
    toStatus: DeviceTransitionStatus,
    newDeviceUdid?: string,
    newDeviceName?: string,
  ): Promise<{status: string}> {
    return this.request(Method.Post, `/devices/${id}/transition`, {
      toStatus,
      newDeviceUdid,
      newDeviceName,
    })
  }

  async getPatients() {
    try {
      const response = await this.request(Method.Get, '/patients')
      return response.map((json: any) => new Patient(json))
    } catch (error) {
      let response = {status: 'error', message: 'Not Provided', code: 500}
      if (error instanceof MaintenanceError) {
        response.message = 'The app is under maintenance, please try again later.'
        response.code = 503
      }
      return response
    }
  }

  async getPatientsPage(page: number = 1, limit: number = 100): Promise<{patients: Patient[], total: number, page: number, limit: number}> {
    const response = await this.request(Method.Get, `/patients/paginated?page=${page}&limit=${limit}`)
    return {
      patients: response.patients.map((json: any) => new Patient(json)),
      total: response.total,
      page: response.page,
      limit: response.limit,
    }
  }

  async addPatient(patient: Patient) {
    const response = await this.request(Method.Post, '/patients', {
      firstName: patient.firstName,
      lastName: patient.lastName,
      physicianName: patient.physicianName,
      birthdate: patient.birthdate.toString(),
    })
    return new Patient(response)
  }

  async deletePatient(id: string) {
    await this.request(Method.Delete, `/patients/${id}`)
  }

  async updateDevice(device: Device): Promise<Device> {
    const response = await this.request(Method.Patch, `/devices/${device.id}`, {
      name: device.name,
      udid: device.udid,
      status: device.status,
    })
    return new Device(response)
  }

  async updatePatient(patient: Patient) {
    const response = await this.request(Method.Patch, `/patients/${patient.id}`, {
      firstName: patient.firstName,
      lastName: patient.lastName,
      physicianName: patient.physicianName,
      birthdate: patient.birthdate.toString(),
      email: patient.email,
    })
    return new Patient(response)
  }

  async resetPatientData(patientId: string) {
    await this.request(Method.Post, `/patients/${patientId}/resetData`)
  }

  async sendResetPasswordInstructions(id: string): Promise<any> {
    return await this.request(
      Method.Post,
      `/passwordRecovery/sendResetPasswordInstructions?userId=${id}`,
    )
  }

  async sendResetPasswordInstructionsByEmail(email: string): Promise<any> {
    return await this.request(
      Method.Post,
      `/passwordRecovery/sendResetPasswordInstructions?email=${encodeURIComponent(email)}`,
    )
  }

  async resetUserPassword(id: string, newPassword: string, resetPasswordToken: string) {
    try {
      return await this.request(Method.Post, `/passwordRecovery/resetPassword?userId=${id}`, {
        passcode: resetPasswordToken,
        newPassword: newPassword,
      })
    } catch (error) {
      let response = {status: 'error', message: 'Not Provided', code: 500}
      if (error instanceof ApiError) {
        if (error.message === 'Incorrect or expired passcode. Please re-enter the passcode.') {
          response.message = 'Your password reset link expired, please request a new one.'
        } else {
          response.message = error.message
        }
        response.code = 400
      }
      return response
    }
  }

  async autoResetUserPassword(id: string) {
    try {
      return await this.request(Method.Patch, `/passwordRecovery/autoResetPassword?userId=${id}`)
    } catch (error) {
      let response = {status: 'error', message: 'Not Provided. Try again later!', code: 500}
      if (error instanceof ApiError) {
        response.message = error.message
        response.code = 400
      }
      return response
    }
  }

  async getAdministratorHistory() {
    const response = await this.request(Method.Get, `/userActivity`)
    return response.map((json: any) => new AdministratorHistory(json))
  }

  async getBulkUploadBatches(page: number = 1, limit: number = 200) {
    return this.request(Method.Get, `/bulkUploadBatches?page=${page}&limit=${limit}`)
  }

  async getBulkUploadBatch(id: string) {
    return this.request(Method.Get, `/bulkUploadBatches/${id}`)
  }

  async getAssignmentsSummary(): Promise<{activeAssignments: number; devicesInInventory: number}> {
    const response = await this.request(Method.Get, '/assignments/summary')
    return {
      activeAssignments: response.active_assignments,
      devicesInInventory: response.devices_in_inventory,
    }
  }

  async uploadCsv(file: File): Promise<CsvImportSuccessSummary> {
    const formData = new FormData()
    formData.append('file', file)

    const headers = this.buildHeaders()
    const url = `${this.environment.url}/csv/upload`
    const response = await fetch(url, {
      method: Method.Post,
      headers,
      body: formData,
    })

    return this.handleResponse(response)
  }

  async validateCsv(file: File): Promise<CsvValidationSummary> {
    if (this.validateAbortController) {
      this.validateAbortController.abort()
    }
    const validateAbortController = new AbortController()
    this.validateAbortController = validateAbortController

    const formData = new FormData()
    formData.append('file', file)

    const headers = this.buildHeaders()
    const url = `${this.environment.url}/csv/validate`
    try {
      const response = await fetch(url, {
        method: Method.Post,
        headers,
        body: formData,
        signal: validateAbortController.signal,
      })
      return this.handleResponse(response)
    } finally {
      if (this.validateAbortController === validateAbortController) {
        this.validateAbortController = undefined
      }
    }
  }

  /** @private */
  async request(
    method: Method,
    endpoint: string,
    json?: object,
    signal?: AbortSignal,
  ): Promise<any> {
    const headers = this.buildHeaders({'Content-Type': 'application/json'})
    const params: RequestInit = {
      method,
      headers,
      signal,
    }
    if (json) {
      params.body = JSON.stringify(json)
    }
    const url = `${this.environment.url}${endpoint}`
    const response = await fetch(url, params)
    return this.handleResponse(response)
  }

  private buildHeaders(extraHeaders?: Record<string, string | undefined>) {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    }
    if (this.token) {
      headers.Authorization = this.token
    }
    if (extraHeaders) {
      Object.entries(extraHeaders).forEach(([key, value]) => {
        if (value !== undefined) {
          headers[key] = value
        }
      })
    }
    return headers
  }

  private async handleResponse(response: Response) {
    if (response.ok) {
      try {
        return await response.json()
      } catch (_) {
        return undefined
      }
    }
    if (response.status === 401) {
      throw UnauthorizedError
    }
    if (response.status === 403) {
      throw ForbiddenError
    }
    if (response.status === 400) {
      try {
        const body = await response.json()
        const message =
          typeof body?.error === 'string' ? body.error : body?.error?.message ?? 'Bad Request'
        const rawDetails = body?.error?.details
        const details: string[] = Array.isArray(rawDetails) ? rawDetails : []
        const batchSummary: CsvImportFailureSummary | undefined = body?.error?.batchSummary
        throw new ApiError(message, details, batchSummary)
      } catch (e) {
        if (e instanceof ApiError) throw e
        throw new ApiError('Bad Request')
      }
    }
    if (response.status === 503) {
      throw new MaintenanceError('The app is under maintenance, please try again later.')
    }
    let errorMessage =
      response.statusText ||
      'There was problem communicating with our servers, please try again later.'
    let errorDetails: string[] = []
    try {
      // Backend error shape: { error: { status: number, message: string, details?: string[] } }
      const errorJson = await response.json()
      errorMessage = errorJson.error?.message ?? errorMessage
      const rawDetails = errorJson.error?.details
      errorDetails = Array.isArray(rawDetails) ? rawDetails : []
    } catch (_) {}

    throw new ApiError(errorMessage, errorDetails)
  }
}

export default Api
